import React, { useState, useEffect, useRef } from "react";
import LazyErrorBoundary from "./LazyErrorBoundary";

/**
 * LazySection
 * Renderiza su contenido solo cuando está por entrar en pantalla (rootMargin: 300px).
 * Deja de observar una vez montada y no se desmonta.
 * Utiliza display: flow-root y position: static para crear un BFC independiente que evita
 * el colapso de márgenes y la superposición entre secciones.
 * Mantiene un placeholder con min-height estimado y marginBlock idéntico antes y después de montar.
 */
const LazySection = ({
  children,
  id,
  minHeight = "350px",
  marginBlock = "2.5rem",
  className = "",
  style = {},
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (isVisible) return;

    // 1. Detección inmediata de coincidencia con hash de ancla
    const checkHashMatch = () => {
      if (typeof window === "undefined") return false;
      const currentHash = (window.location.hash || "").replace("#", "");
      if (id && currentHash && id.toLowerCase() === currentHash.toLowerCase()) {
        setIsVisible(true);
        return true;
      }
      return false;
    };

    if (checkHashMatch()) return;

    const onHashChange = () => {
      checkHashMatch();
    };
    window.addEventListener("hashchange", onHashChange);

    // 2. Fallback si el navegador no cuenta con IntersectionObserver
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      const fallbackTimer = setTimeout(() => setIsVisible(true), 1000);
      return () => {
        clearTimeout(fallbackTimer);
        window.removeEventListener("hashchange", onHashChange);
      };
    }

    // 3. IntersectionObserver con margen de 300px
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry && (entry.isIntersecting || entry.intersectionRatio > 0)) {
          setIsVisible(true);
          if (containerRef.current) {
            observer.unobserve(containerRef.current);
          }
        }
      },
      {
        root: null,
        rootMargin: "300px 0px",
        threshold: 0,
      }
    );

    const el = containerRef.current;
    if (el) {
      observer.observe(el);
    }

    return () => {
      if (el) {
        observer.unobserve(el);
      }
      observer.disconnect();
      window.removeEventListener("hashchange", onHashChange);
    };
  }, [id, isVisible]);

  return (
    <section
      ref={containerRef}
      id={id}
      className={`lazy-section-wrapper ${className}`.trim()}
      style={{
        display: "flow-root",
        position: "static",
        width: "100%",
        boxSizing: "border-box",
        marginBlock,
        ...style,
      }}
    >
      <LazyErrorBoundary minHeight={minHeight}>
        {isVisible ? (
          children
        ) : (
          <div
            className="lazy-section-placeholder"
            style={{
              minHeight,
              width: "100%",
              display: "block",
              boxSizing: "border-box",
            }}
            aria-hidden="true"
          />
        )}
      </LazyErrorBoundary>
    </section>
  );
};

export default LazySection;
